/**
 * A team's rotation: who takes new people, in what order, how many each,
 * and when they work.
 *
 * Underneath it is a route group on a stream whose destinations are reps,
 * run by the same engine that sells leads, after every selling tier and only
 * for leads nobody bought. It never sells. These screens build one without
 * anyone having to think in groups and destinations: pick a stream, a team
 * and how to take turns, then set each rep's caps, days and hours.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  ActionIcon, Alert, Badge, Button, Card, Chip, Group, Modal, NumberInput, SegmentedControl, Select, Stack,
  Switch, Table, Text, TextInput, Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconPencil, IconPlus, IconTrash, IconUsersGroup } from '@tabler/icons-react';
import {
  METHODS, repInput, useAssignmentGroups, useCreateAssignmentGroup, useDeleteAssignmentGroup,
  useSetAssignmentReps, useUpdateAssignmentGroup,
  type AssignmentGroup, type AssignmentRepInput,
} from '../../api/assignment';
import { useCampaigns } from '../../api/queries';
import { useTeams, type Team } from '../../api/teams';
import { useAuth } from '../../auth/AuthProvider';
import { TOKENS } from '../../theme';
import { MonoLabel } from '../../ui';
import { useVocab } from '../../vocab';
import { useOwnerOptions } from '../crm/crmShared';
import { UsedByPanel } from '../workflows/UsedByPanel';

const DAYS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const HOURS = Array.from({ length: 25 }, (_, h) => ({ value: String(h), label: `${String(h).padStart(2, '0')}:00` }));

export function methodLabel(method: string): string {
  return METHODS.find((m) => m.value === method)?.label ?? method;
}

/** "Mon to Fri, 09:00 to 17:30" in a few words; "Every day, all day" when unset. */
export function workingWords(days: number[] | null | undefined, hours: { start: number; end: number } | null | undefined) {
  const d = !days || days.length === 0 || days.length === 7
    ? 'Every day'
    : days.length === 5 && [0, 1, 2, 3, 4].every((x) => days.includes(x))
      ? 'Weekdays'
      : days.slice().sort().map((x) => DAY_NAMES[x]?.slice(0, 3)).join(', ');
  const h = !hours || hours.start === hours.end
    ? 'all day'
    : `${String(hours.start).padStart(2, '0')}:00 to ${String(hours.end).padStart(2, '0')}:00`;
  return `${d}, ${h}`;
}

/** One rep's row in the editor. */
function RepRow({
  rep, weighted, names, onChange, onRemove,
}: {
  rep: AssignmentRepInput;
  weighted: boolean;
  names: Map<string, string>;
  onChange: (r: AssignmentRepInput) => void;
  onRemove: () => void;
}) {
  const days = rep.active_days ?? [];
  const hours = rep.active_hours ?? null;
  const allDay = !hours || hours.start === hours.end;
  const name = names.get(rep.user_id) ?? rep.user_id;
  return (
    <Table.Tr data-testid={`rep-${rep.user_id}`}>
      <Table.Td>
        <Group gap="xs" wrap="nowrap">
          <Switch
            size="xs"
            checked={rep.active !== false}
            onChange={(e) => onChange({ ...rep, active: e.currentTarget.checked })}
            aria-label={`${name} takes people`}
          />
          <Text size="sm" fw={500}>{name}</Text>
        </Group>
      </Table.Td>
      {weighted && (
        <Table.Td>
          <NumberInput
            size="xs" w={60} min={1} hideControls aria-label={`${name} weight`}
            value={rep.weight ?? 1}
            onChange={(n) => onChange({ ...rep, weight: Number(n) || 1 })}
          />
        </Table.Td>
      )}
      <Table.Td>
        <NumberInput
          size="xs" w={64} min={0} hideControls placeholder="None" aria-label={`${name} daily cap`}
          value={rep.daily_cap || ''}
          onChange={(n) => onChange({ ...rep, daily_cap: Number(n) || 0 })}
        />
      </Table.Td>
      <Table.Td>
        <NumberInput
          size="xs" w={64} min={0} hideControls placeholder="None" aria-label={`${name} weekly cap`}
          value={rep.weekly_cap || ''}
          onChange={(n) => onChange({ ...rep, weekly_cap: Number(n) || 0 })}
        />
      </Table.Td>
      <Table.Td>
        <Chip.Group
          multiple
          value={days.map(String)}
          onChange={(v) => onChange({ ...rep, active_days: v.map(Number).sort() })}
        >
          <Group gap={2} wrap="nowrap" aria-label={`${name} working days`}>
            {DAYS.map((d, i) => (
              <Tooltip key={i} label={DAY_NAMES[i]} openDelay={400}>
                <Chip value={String(i)} size="xs" radius="sm" variant="filled" styles={{ iconWrapper: { display: 'none' }, label: { width: 30, paddingInline: 0, justifyContent: 'center' } }}>{d}</Chip>
              </Tooltip>
            ))}
          </Group>
        </Chip.Group>
      </Table.Td>
      <Table.Td>
        <Group gap={4} wrap="nowrap">
          <Select
            size="xs" w={84} aria-label={`${name} starts`}
            data={HOURS.slice(0, 24)}
            value={allDay ? null : String(hours!.start)}
            placeholder="All day"
            clearable
            onChange={(s) => onChange({
              ...rep,
              active_hours: s === null ? null : { start: Number(s), end: allDay ? 17 : hours!.end, timezone: 'Europe/London' },
            })}
          />
          <Text size="xs" c="dimmed">to</Text>
          <Select
            size="xs" w={84} aria-label={`${name} ends`}
            data={HOURS.slice(1)}
            value={allDay ? null : String(hours!.end)}
            placeholder="All day"
            disabled={allDay}
            onChange={(s) => s && onChange({
              ...rep,
              active_hours: { start: hours?.start ?? 9, end: Number(s), timezone: 'Europe/London' },
            })}
          />
        </Group>
      </Table.Td>
      <Table.Td>
        <ActionIcon variant="subtle" color="gray" onClick={onRemove} aria-label={`Remove ${name}`}>
          <IconTrash size={15} />
        </ActionIcon>
      </Table.Td>
    </Table.Tr>
  );
}

/**
 * Edit a rotation: how it takes turns, what each assignment does, the
 * team's caps, and each rep's caps, days and hours. Saved in one go.
 */
export function RotationEditor({ group, onClose }: { group: AssignmentGroup | null; onClose: () => void }) {
  const v = useVocab();
  const owners = useOwnerOptions(!!group);
  const names = useMemo(() => new Map(owners.map((o) => [o.value, o.label.replace(/ \(you\)$/, '')])), [owners]);
  const update = useUpdateAssignmentGroup();
  const setReps = useSetAssignmentReps();
  const [name, setName] = useState('');
  const [method, setMethod] = useState('round-robin');
  const [active, setActive] = useState(true);
  const [task, setTask] = useState(true);
  const [due, setDue] = useState<number>(15);
  const [notify, setNotify] = useState(true);
  const [teamDaily, setTeamDaily] = useState<number>(0);
  const [reps, setRepsState] = useState<AssignmentRepInput[]>([]);
  const [adding, setAdding] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!group) return;
    setName(group.name);
    setMethod(group.distribution_method);
    setActive(group.active);
    setTask(group.create_task);
    setDue(group.task_due_minutes);
    setNotify(group.notify);
    setTeamDaily(group.caps.daily_cap);
    setRepsState(group.reps.map(repInput));
    // Names from the rotation itself, for reps the picker has not loaded.
    for (const r of group.reps) if (!names.has(r.user_id)) names.set(r.user_id, r.user_name);
    setError(null);
  }, [group]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!group) return null;
  const available = owners.filter((o) => !reps.some((r) => r.user_id === o.value));

  async function save() {
    if (!group) return;
    setError(null);
    try {
      await update.mutateAsync({
        id: group.id,
        body: {
          name, distribution_method: method, active, create_task: task, task_due_minutes: due, notify,
          daily_cap: teamDaily,
        },
      });
      await setReps.mutateAsync({ id: group.id, reps: reps.map((r, i) => ({ ...r, priority: i + 1 })) });
      notifications.show({ color: 'teal', title: 'Rotation saved', message: `${name} is up to date.` });
      onClose();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <Modal opened={!!group} onClose={onClose} title={`Rotation: ${group.name}`} size={1000} data-testid="rotation-editor">
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          {`On ${group.campaign_name}, after every selling ${v.one('routeGroup')}: ${v.many('lead')} nobody bought go to the next rep who can take them. A rotation never sells.`}
        </Text>
        <Group grow align="flex-end">
          <TextInput label="Name" value={name} onChange={(e) => setName(e.currentTarget.value)} />
          <Switch label="On" checked={active} onChange={(e) => setActive(e.currentTarget.checked)} />
        </Group>
        <div>
          <MonoLabel>How reps take turns</MonoLabel>
          <SegmentedControl
            mt={6}
            value={method}
            onChange={setMethod}
            data={METHODS.map((m) => ({ value: m.value, label: m.label }))}
            aria-label="How reps take turns"
          />
          <Text size="xs" c="dimmed" mt={4}>{METHODS.find((m) => m.value === method)?.hint}</Text>
        </div>
        <Group gap="lg" align="flex-end">
          <Switch label={`Make a ${v.one('task')} for the rep`} checked={task} onChange={(e) => setTask(e.currentTarget.checked)} />
          <NumberInput
            label="Due after (minutes)" w={170} min={0} disabled={!task}
            value={due} onChange={(n) => setDue(Number(n) || 0)}
          />
          <Switch label="Notify the rep" checked={notify} onChange={(e) => setNotify(e.currentTarget.checked)} />
          <NumberInput
            label="Team daily cap" w={140} min={0} placeholder="None"
            value={teamDaily || ''} onChange={(n) => setTeamDaily(Number(n) || 0)}
          />
        </Group>

        <Card withBorder p={0}>
          <Table.ScrollContainer minWidth={760}>
            <Table verticalSpacing={6} data-testid="rotation-reps">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Rep</Table.Th>
                  {method === 'weighted' && <Table.Th>Weight</Table.Th>}
                  <Table.Th>Daily cap</Table.Th>
                  <Table.Th>Weekly cap</Table.Th>
                  <Table.Th>Days</Table.Th>
                  <Table.Th>Hours (UK)</Table.Th>
                  <Table.Th w={40} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {reps.length === 0 && (
                  <Table.Tr><Table.Td colSpan={7}>
                    <Text size="sm" c="dimmed" ta="center" py="sm">No reps yet, so nobody is given anyone.</Text>
                  </Table.Td></Table.Tr>
                )}
                {reps.map((r, i) => (
                  <RepRow
                    key={r.user_id}
                    rep={r}
                    weighted={method === 'weighted'}
                    names={names}
                    onChange={(next) => setRepsState((all) => all.map((x, j) => (j === i ? next : x)))}
                    onRemove={() => setRepsState((all) => all.filter((_, j) => j !== i))}
                  />
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Card>
        <Group gap="xs">
          <Select
            placeholder="Add a rep"
            searchable
            data={available}
            value={adding}
            onChange={setAdding}
            w={240}
            aria-label="Add a rep"
            nothingFoundMessage="Everyone is in already"
          />
          <Button
            variant="default"
            leftSection={<IconPlus size={14} />}
            disabled={!adding}
            onClick={() => {
              if (!adding) return;
              setRepsState((all) => [...all, { user_id: adding, active: true, weight: 1, daily_cap: 0, weekly_cap: 0, monthly_cap: 0, active_days: [], active_hours: null }]);
              setAdding(null);
            }}
          >
            Add
          </Button>
        </Group>
        {error && <Alert color="red" variant="light">{error}</Alert>}
        <UsedByPanel
          refType="rotation"
          refId={group.id}
          title={`${v.Many('workflow')} that assign through it`}
          empty={`No ${v.many('workflow')} assign through this rotation. Routing still sends unsold ${v.many('lead')} to it.`}
          testId="rotation-used-by"
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button onClick={save} loading={update.isPending || setReps.isPending}>Save rotation</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/**
 * A new rotation: a name, the stream whose unsold leads it takes, the team
 * whose members become its reps, and how they take turns.
 */
export function NewRotationModal({
  opened, onClose, campaignId, onCreated,
}: {
  opened: boolean;
  onClose: () => void;
  /** Fixed when opened from a stream's delivery tab. */
  campaignId?: string;
  onCreated?: (g: AssignmentGroup) => void;
}) {
  const v = useVocab();
  const { user } = useAuth();
  const campaigns = useCampaigns(opened && !campaignId).data?.campaigns ?? [];
  const teams = useTeams(user?.tenant_id, opened).data?.teams ?? [];
  const owners = useOwnerOptions(opened);
  const create = useCreateAssignmentGroup();
  const [name, setName] = useState('');
  const [campaign, setCampaign] = useState<string | null>(campaignId ?? null);
  const [team, setTeam] = useState<string | null>(null);
  const [method, setMethod] = useState('round-robin');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (opened) {
      setName('');
      setCampaign(campaignId ?? null);
      setTeam(null);
      setMethod('round-robin');
      setError(null);
    }
  }, [opened, campaignId]);

  const chosen: Team | undefined = teams.find((t) => t.id === team);
  // Only members who have signed in here can be given people.
  const signedIn = new Set(owners.map((o) => o.value));
  const reps = (chosen?.members ?? []).filter((m) => signedIn.has(m.user_id));

  async function submit() {
    setError(null);
    try {
      const g = await create.mutateAsync({
        campaign_id: campaign ?? '',
        name: name.trim() || (chosen ? `${chosen.name} rotation` : ''),
        team_id: team,
        distribution_method: method,
        reps: reps.map((m) => ({ user_id: m.user_id, active: true })),
      });
      notifications.show({ color: 'teal', title: 'Rotation added', message: `${g.name} takes unsold ${v.many('lead')} on ${g.campaign_name}.` });
      onCreated?.(g);
      onClose();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} title="New rotation" size="md">
      <Stack gap="sm">
        {!campaignId && (
          <Select
            label={v.One('campaign')}
            description={`Its unsold ${v.many('lead')} go to the rotation as they arrive.`}
            data={campaigns.map((c) => ({ value: c.id, label: c.name }))}
            value={campaign}
            onChange={setCampaign}
            searchable
          />
        )}
        <Select
          label={v.One('team')}
          description="Its members who have signed in become the reps."
          data={teams.map((t) => ({ value: t.id, label: t.name }))}
          value={team}
          onChange={setTeam}
          clearable
          placeholder="Choose reps later"
        />
        {chosen && (
          <Text size="xs" c="dimmed" data-testid="new-rotation-reps">
            {reps.length === 0
              ? 'Nobody in this team has signed in yet.'
              : `Reps: ${reps.map((m) => [m.first_name, m.last_name].filter(Boolean).join(' ') || m.email).join(', ')}.`}
          </Text>
        )}
        <TextInput
          label="Name"
          placeholder={chosen ? `${chosen.name} rotation` : 'North team rotation'}
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
        />
        <SegmentedControl
          value={method}
          onChange={setMethod}
          data={METHODS.map((m) => ({ value: m.value, label: m.label }))}
          aria-label="How reps take turns"
        />
        {error && <Alert color="red" variant="light">{error}</Alert>}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} loading={create.isPending} disabled={!campaign || (!name.trim() && !chosen)}>
            Add rotation
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** One rotation as a row of the Assignment list. */
function RotationRow({ g, onEdit, onDelete }: { g: AssignmentGroup; onEdit: () => void; onDelete: () => void }) {
  const on = g.reps.filter((r) => r.active).length;
  return (
    <Table.Tr data-testid={`rotation-${g.id}`} opacity={g.active ? 1 : 0.6}>
      <Table.Td><Text size="sm" fw={500}>{g.name}</Text></Table.Td>
      <Table.Td><Text size="sm">{g.campaign_name}</Text></Table.Td>
      <Table.Td><Badge variant="light" color="gray" tt="none">{methodLabel(g.distribution_method)}</Badge></Table.Td>
      <Table.Td>
        <Text size="sm">{g.reps.length === 0 ? 'No reps' : `${on} of ${g.reps.length} on`}</Text>
        <Text size="xs" c="dimmed" lineClamp={1}>{g.reps.map((r) => r.user_name).join(', ')}</Text>
      </Table.Td>
      <Table.Td>{g.active ? <Badge color="chartGreen" variant="light">On</Badge> : <Badge color="gray">Off</Badge>}</Table.Td>
      <Table.Td>
        <Group gap={4} wrap="nowrap" justify="flex-end">
          <ActionIcon variant="subtle" color="gray" onClick={onEdit} aria-label={`Edit ${g.name}`}><IconPencil size={15} /></ActionIcon>
          <ActionIcon variant="subtle" color="gray" onClick={onDelete} aria-label={`Delete ${g.name}`}><IconTrash size={15} /></ActionIcon>
        </Group>
      </Table.Td>
    </Table.Tr>
  );
}

/** Every rotation in the organisation, to add, edit or delete. */
export function RotationsPanel() {
  const v = useVocab();
  const list = useAssignmentGroups();
  const remove = useDeleteAssignmentGroup();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<AssignmentGroup | null>(null);
  const groups = list.data?.groups ?? [];
  return (
    <Card withBorder p="md" data-testid="rotations-panel">
      <Group justify="space-between" mb="sm">
        <div>
          <Group gap="xs"><IconUsersGroup size={18} /><Text fw={600}>Assignment</Text></Group>
          <Text size="sm" c="dimmed" maw={640}>
            {`Rotations give new ${v.many('person')} to reps, in turn, within each rep's caps, days and hours. Each one is the last ${v.one('routeGroup')} of a ${v.one('campaign')}, for ${v.many('lead')} nobody bought, and can also take ${v.many('person')} you send it from the records page.`}
          </Text>
        </div>
        <Button leftSection={<IconPlus size={14} />} onClick={() => setAdding(true)}>New rotation</Button>
      </Group>
      {list.error && <Alert color="red" variant="light">{list.error.message}</Alert>}
      <Table.ScrollContainer minWidth={680}>
        <Table verticalSpacing={8}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Rotation</Table.Th>
              <Table.Th>{v.One('campaign')}</Table.Th>
              <Table.Th>Takes turns</Table.Th>
              <Table.Th>Reps</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {groups.length === 0 && (
              <Table.Tr><Table.Td colSpan={6}>
                <Text size="sm" c="dimmed" ta="center" py="sm">No rotations yet.</Text>
              </Table.Td></Table.Tr>
            )}
            {groups.map((g) => (
              <RotationRow
                key={g.id}
                g={g}
                onEdit={() => setEditing(g)}
                onDelete={() => {
                  if (window.confirm(`Delete ${g.name}? People it already gave out keep their owners.`)) remove.mutate(g.id);
                }}
              />
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      <NewRotationModal opened={adding} onClose={() => setAdding(false)} onCreated={(g) => setEditing(g)} />
      <RotationEditor group={editing} onClose={() => setEditing(null)} />
    </Card>
  );
}

/**
 * A stream's assignment tier on its delivery tab: a different card from the
 * selling tiers, because it never sells and always runs last.
 */
export function AssignmentTierCard({ group, canEdit }: {
  group: { id: string; name: string; distribution_method: string; active: boolean };
  canEdit: boolean;
}) {
  const v = useVocab();
  const list = useAssignmentGroups();
  const full = list.data?.groups.find((g) => g.id === group.id) ?? null;
  const [editing, setEditing] = useState(false);
  return (
    <Card withBorder padding="md" opacity={group.active ? 1 : 0.6} data-testid={`assignment-tier-${group.id}`}
      style={{ borderStyle: 'dashed', borderColor: TOKENS.borderStrong }}>
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Stack gap={4}>
          <Group gap="sm">
            <MonoLabel style={{ background: TOKENS.surface2, border: `1px solid ${TOKENS.border}`, borderRadius: 6, padding: '1px 7px' }}>
              Assignment
            </MonoLabel>
            <Text fw={600}>{group.name}</Text>
            <Badge variant="light" color="gray" tt="none">{methodLabel(group.distribution_method)}</Badge>
            {!group.active && <Badge size="sm" color="gray">off</Badge>}
          </Group>
          <Text size="xs" c="dimmed">
            {`Runs after every selling ${v.one('routeGroup')}, for ${v.many('lead')} nobody bought: the ${v.one('person')} goes to the next rep who can take them. Never sells.`}
          </Text>
        </Stack>
        {canEdit && (
          <Button size="compact-sm" variant="default" leftSection={<IconPencil size={14} />} onClick={() => setEditing(true)} disabled={!full}>
            Edit rotation
          </Button>
        )}
      </Group>
      {full && (
        <Table mt="md" verticalSpacing={6}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Rep</Table.Th>
              <Table.Th>Today</Table.Th>
              <Table.Th>Works</Table.Th>
              <Table.Th>Status</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {full.reps.map((r) => (
              <Table.Tr key={r.link_id} opacity={r.active ? 1 : 0.55}>
                <Table.Td><Text size="sm" fw={500}>{r.user_name}</Text></Table.Td>
                <Table.Td>
                  <Text size="sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {r.caps.daily_cap ? `${r.caps.daily_count} of ${r.caps.daily_cap}` : `${r.caps.daily_count}, no cap`}
                  </Text>
                </Table.Td>
                <Table.Td><Text size="sm">{workingWords(r.active_days, r.active_hours)}</Text></Table.Td>
                <Table.Td><Badge variant="light" color={r.active ? 'chartGreen' : 'gray'}>{r.active ? 'On' : 'Off'}</Badge></Table.Td>
              </Table.Tr>
            ))}
            {full.reps.length === 0 && (
              <Table.Tr><Table.Td colSpan={4}><Text size="sm" c="dimmed">No reps yet.</Text></Table.Td></Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      )}
      <RotationEditor group={editing ? full : null} onClose={() => setEditing(false)} />
    </Card>
  );
}
